import React, { useState, useCallback, useContext } from 'react';
import { useDropzone } from 'react-dropzone';
import cl from './ImageUploader.module.css'
import imageCompression from 'browser-image-compression';
import { AppContext } from '../../../providers/appProvider';
import { baseFileURL } from '../../../Api';

const ImageTile = React.memo(({ index, image, onDelete, hasDeletePermission, onClick }) => (
    <div key={index} className={cl.image}  >
        <div  className={cl.image} onClick={onClick}>
            <img src={`${baseFileURL}${image}`} alt={`Uploaded ${image}`}  /> 
        </div>
        {hasDeletePermission && (
            <span className={cl.dlt} onClick={() => onDelete(index)}></span>
        )}
    </div>
));

function ImageUploader({ images, uploadImage, deleteImage, kind, title, disabled }) {
    const [isLoading, setIsLoading] = useState(false);
    const { appState, app } = useContext(AppContext);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalImage, setModalImage] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [galleryImages, setGalleryImages] = useState([]);

    const openModal = (images, index) => {
        setCurrentImageIndex(index);
        setGalleryImages(images);
        setModalImage(images[index]);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setModalImage(null);
    };

    const nextImage = () => {
        const newIndex = (currentImageIndex + 1) % galleryImages.length;
        setCurrentImageIndex(newIndex);
        setModalImage(galleryImages[newIndex]);
    };

    const prevImage = () => {
        const newIndex = (currentImageIndex - 1 + galleryImages.length) % galleryImages.length;
        setCurrentImageIndex(newIndex);
        setModalImage(galleryImages[newIndex]);
    };

    const onDrop = useCallback(async (acceptedFiles) => {
        setIsLoading(true);
        const options = { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true };
        const validExtensions = ['.jpg', '.jpeg', '.png'];

        let validFiles = [];
        let invalidFilesCount = 0;

        acceptedFiles.forEach(file => {
            if (validExtensions.includes(file.name.toLowerCase().slice(file.name.lastIndexOf('.')))) {
                validFiles.push(file);
            } else {
                invalidFilesCount++;
            }
        });

        if (invalidFilesCount > 0) {
            app.setError(`${invalidFilesCount} ფაილი არ არის სურათის ფორმატის.`);
        }

        try {
            const compressedFiles = await Promise.all(
                validFiles.map(file => imageCompression(file, options))
            );
            await uploadImage(kind, compressedFiles);
        } catch (error) {
            console.error("Error compressing image:", error);
        }
        setIsLoading(false);
    }, [uploadImage, kind]);

    const handleDelete = useCallback((index) => {
        const imageToDelete = images[index];
        deleteImage(kind, imageToDelete);
    }, [images, deleteImage, kind]);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: '.jpg, .jpeg, .png',
        noClick: disabled,
        noDrag: disabled,
    });

    return (
        <div className={cl.boxUpload}>
            <div className={cl.title}>{title}</div>
            <div {...getRootProps()} className={cl.clickDiv}>
                <input {...getInputProps()} />
                <div className={cl.message}>ჩააგდეთ სურათები <br /><span>ან დააკლიკეთ</span></div>
            </div>
            <div className={cl.container}>
                {images.map((image, index) => (
                    <ImageTile 
                        key={index} 
                        image={image} 
                        index={index} 
                        onDelete={handleDelete} 
                        hasDeletePermission={app.hasPermission("PICTURE_DELETE")}
                        onClick={() => openModal(images, index)}  
                    />
                ))}
            </div>
            {isLoading && (
                <div className={cl.loadingoverlay}>
                    <div className={cl.loadingspinner}></div>
                </div>
            )}
            {isModalOpen && (
                <div className={cl.modal} onClick={closeModal}>
                    <span className={cl.modalClose} onClick={closeModal}>
                        <svg aria-hidden="true" viewBox="0 0 24 24">
                            <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 6 6 18M6 6l12 12"></path>
                        </svg>
                    </span>
                    <div className={cl.modalContent} onClick={(e) => e.stopPropagation()}>
                        {currentImageIndex > 0 && (
                            <button className={cl.modalNav + ' ' + cl.modalNavPrev} onClick={prevImage}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
                                    <path fill="currentColor" d="M10 22L0 12L10 2l1.775 1.775L3.55 12l8.225 8.225z"/>
                                </svg>
                            </button>
                        )}
                        <img src={`${baseFileURL}${modalImage}`} alt="Modal" className={cl.modalImage} />
                        {currentImageIndex < galleryImages.length - 1 && (
                            <button className={cl.modalNav + ' ' + cl.modalNavNext} onClick={nextImage}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
                                    <path fill="currentColor" d="M10 22L0 12L10 2l1.775 1.775L3.55 12l8.225 8.225z"/>
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ImageUploader;
